import React from 'react';

import { Text } from '@/components/ui/text';

export interface HighlightSegment {
  text: string;
  isMatch: boolean;
}

/**
 * Splits text by query, case-insensitive. Uses indexOf so safe for user input
 * with special characters, unlike regex.
 *
 * Returns segments with isMatch flag. Empty query returns single non-match segment.
 */
export function getHighlightSegments(text: string, query: string): HighlightSegment[] {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return [{ text, isMatch: false }];
  }

  const lowerText = text.toLowerCase();

  const lowerQuery = trimmedQuery.toLowerCase();

  const segments: HighlightSegment[] = [];

  let start = 0;

  let idx: number;

  while ((idx = lowerText.indexOf(lowerQuery, start)) !== -1) {
    if (idx > start) {
      segments.push({ text: text.slice(start, idx), isMatch: false });
    }

    segments.push({
      text: text.slice(idx, idx + trimmedQuery.length),
      isMatch: true,
    });

    start = idx + trimmedQuery.length;
  }

  if (start < text.length) {
    segments.push({ text: text.slice(start), isMatch: false });
  }

  return segments;
}

/**
 * Renders text with matching segments wrapped in bold Text.
 *
 * Example: highlightMatch with Multiple Myeloma and my yields Multiple plus bold My plus eloma
 * plus space plus bold My plus eloma.
 *
 * Optional className is applied to the wrapper and inherited by bold segments so styling
 * matches non-search state.
 */
export function highlightMatch(text: string, query: string, className?: string): React.ReactNode {
  const segments = getHighlightSegments(text, query);

  if (segments.length === 1 && !segments[0].isMatch) {
    return text;
  }

  return (
    <Text className={className}>
      {segments.map((seg, i) =>
        seg.isMatch ? (
          <Text key={i} bold className={className}>
            {seg.text}
          </Text>
        ) : (
          seg.text
        )
      )}
    </Text>
  );
}
